import React, { memo, useEffect, useState } from 'react';
import { getBezierPath, Position } from '@xyflow/react';
import { GradientAndGlowDefs } from './SharedStyles';
import { useObservable } from '@/core/U';
import { Config } from '@/core/Config';
import { useNodeViewModel } from '@/views/flow/nodeview/NodeViewHook';
import { analyzePresignedImageColor } from '@/utils/presignedImageAnalysis';
import { NodeType } from '@/views/flow/nodeview/NodeItem';
import { useTheme } from 'next-themes';

// const DEFAULT_COLOR = '#3A3C42'; // 기본 단색
const DEFAULT_COLOR = '#FF3496'; // 기본 단색

export const CustomEdge = memo(
  ({ id, sourceX, sourceY, targetX, targetY, state, source, target, selected, data }: any) => {
    //   const edgeStyles = useEdgeStyleStore((state) => state.edgeStyles)

    const { resolvedTheme } = useTheme();

    const edgeStyle: any = data?.edgeStyle ? data.edgeStyle : 'inactive';
    const edgeState: any = data?.state ? data.state : 'inactive';

    const edge_color = useObservable(Config.getInstance().edge_color);
    const edge_thickness = useObservable(Config.getInstance().edge_thickness);

    const epsilon = 0.1;
    const adjustedSourceY = sourceY + epsilon;
    const adjustedTargetY = targetY - epsilon;
    const offset = 12 / 2; // HANDLER_SIZE / 2;
    const adjustedSourceX = sourceX - offset;
    const adjustedTargetX = targetX + offset;
    const [path] = getBezierPath({
      targetX: adjustedTargetX,
      targetY: adjustedTargetY,
      targetPosition: Position.Left,
      sourceX: adjustedSourceX,
      sourceY: adjustedSourceY,
      sourcePosition: Position.Right,
      // curvature: 0.8,
    });

    // source, target이 각각 노드 id
    const director = useNodeViewModel();
    const nodes = useObservable(director.data.nodes);

    const [currentStyle, setCurrentStyle] = useState({
      strokeValue: edge_color,
      // filterValue: 'none',
    });
    const [sourceColor, setSourceColor] = useState(DEFAULT_COLOR);
    const [targetColor, setTargetColor] = useState(DEFAULT_COLOR);
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
      const sourceNode = nodes.find((n) => n.id == source);
      const targetNode = nodes.find((n) => n.id == target);
      let newSourceColor = edge_color === 'gradient' ? DEFAULT_COLOR : edge_color;
      let newTargetColor = edge_color === 'gradient' ? DEFAULT_COLOR : edge_color;
      if (sourceNode) {
        if (sourceNode.type === NodeType.DRAWING) newSourceColor = '#b0b6b7';
        else if (sourceNode.dominantColor.all.length > 0)
          newSourceColor = sourceNode.dominantColor.all;
      }
      if (targetNode) {
        if (targetNode.type === NodeType.DRAWING) newTargetColor = '#b0b6b7';
        else if (targetNode.dominantColor.all.length > 0)
          newTargetColor = targetNode.dominantColor.all;
      }
      setSourceColor(newSourceColor);
      setTargetColor(newTargetColor);

      setCurrentStyle({
        strokeValue: edge_color === 'gradient' ? `url(#gradient-${id})` : edge_color,
        // filterValue: `url(#glow-${id})`,
        // filterValue:
        //   selected || edgeStyle === 'active' || edgeState === 'active' || edge_color === 'gradient'
        //     ? `url(#glow-${id})`
        //     : 'none',
      });

      setAnimate(selected || edgeStyle === 'active' || edgeState === 'active');
    }, [id, selected, edgeStyle, edgeState]);

    const getPathMarker = (x: number, y: number, left: boolean = false) => {
      const unit = edge_thickness * 2 > 20 ? edge_thickness * 2 : 20;
      const xLeft = 4;

      const x1 = x;
      const y1 = y - unit / 2;
      const x2 = x + (left ? xLeft : -xLeft);
      const y2 = y;
      const x3 = x;
      const y3 = y + unit / 2;

      return `M ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3}`;
    };

    return (
      <>
        <GradientAndGlowDefs id={id} sourceColor={sourceColor} targetColor={targetColor} />
        {selected && (
          <path
            d={path}
            stroke={resolvedTheme === 'dark' ? 'white' : '#00000066'}
            strokeWidth={edge_thickness + 4}
            strokeOpacity={0.4}
            fill="none"
            shapeRendering="geometricPrecision"
          />
        )}
        <path
          d={path}
          stroke={currentStyle.strokeValue}
          strokeWidth={edge_thickness}
          fill="none"
          shapeRendering="geometricPrecision"
        />
        {animate && (
          <path
            d={path}
            stroke={`url(#animate-${id})`}
            strokeWidth={edge_thickness}
            fill="none"
            shapeRendering="geometricPrecision"
          />
        )}

        {/* <filter id="myBlurFilter">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="1.05" result="effect1_foregroundBlur_559_8799" />
        </filter>
        <filter id="glow">
          <feBlend in="SourceGraphic" in2="shadow" />
          <feDropShadow dx="0" dy="0" stdDeviation="0.5" floodColor="white" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
        </filter>
        <path filter="url(#glow)" d={getPathMarker(adjustedTargetX, targetY)} fill="white" />
        <path filter="url(#glow)" d={getPathMarker(adjustedSourceX, sourceY, true)} fill="white" /> */}
      </>
    );
  },
);

/**
 * 이미지의 평균 색상을 계산합니다.
 *
 * ⚠️ CORS 요구사항:
 * - imgElement는 반드시 crossOrigin='anonymous'로 로드되어야 합니다
 * - S3 등 cross-origin 이미지의 경우 CORS 미설정 시 getImageData()에서 SecurityError 발생
 * - blob: URL 또는 data: URL은 CORS 설정 불필요
 *
 * @param imgElement - 이미지 엘리먼트 (cross-origin인 경우 CORS 필수)
 * @param callback - RGB 색상 문자열을 받는 콜백 함수
 */
export function getAverageColor(imgElement: HTMLImageElement, callback: (c: string) => void) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = imgElement.width;
  canvas.height = imgElement.height;
  if (!context) return '';
  context.drawImage(imgElement, 0, 0);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  let r = 0;
  let g = 0;
  let b = 0;

  for (let i = 0; i < data.length; i += 4) {
    r += data[i]; // R 값
    g += data[i + 1]; // G 값
    b += data[i + 2]; // B 값
  }

  const totalPixels = data.length / 4;
  const avgR = Math.round(r / totalPixels);
  const avgG = Math.round(g / totalPixels);
  const avgB = Math.round(b / totalPixels);

  callback(`rgb(${avgR}, ${avgG}, ${avgB})`);
}

export function handleGetImgColor(
  imageUrl: string,
  callback: (c: string) => void,
  signal?: AbortSignal,
) {
  analyzePresignedImageColor(imageUrl, callback, 0, signal);
}

// 색상 문자열을 RGB 값으로 파싱하는 함수
const parseColor = (color: string): { r: number; g: number; b: number } | null => {
  // hex 형태 (#RRGGBB 또는 #RGB)
  if (color.startsWith('#')) {
    const hex = color.slice(1);

    if (hex.length === 3) {
      // #RGB -> #RRGGBB 형태로 변환
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b };
    } else if (hex.length === 6) {
      // #RRGGBB
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b };
    }
  }

  // rgb 또는 rgba 형태
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1]),
      g: parseInt(rgbMatch[2]),
      b: parseInt(rgbMatch[3]),
    };
  }

  return null;
};

// W3C 표준 luminance 계산 함수
const calculateLuminanceFromRGB = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// 특정 색상에 대해 최적의 텍스트 색상을 반환하는 함수
export const getOptimalTextColor = (backgroundColor: string): 'black' | 'white' => {
  const parsedColor = parseColor(backgroundColor);

  if (!parsedColor) {
    // 파싱 실패시 기본값 (어두운 배경 가정)
    return 'white';
  }

  const luminance = calculateLuminanceFromRGB(parsedColor.r, parsedColor.g, parsedColor.b);

  // luminance가 0.5보다 크면 밝은 배경이므로 검은 텍스트
  // 0.5보다 작으면 어두운 배경이므로 흰 텍스트
  return luminance > 0.5 ? 'black' : 'white';
};
